import type { Code99Event, Code99Session } from '@/types';

/**
 * Code 99 compliance checker.
 *
 * Per addendum § 1.3, Code 99 Mode cannot close without certain
 * documentation elements in place. This module is the single source
 * of truth for those rules. Both the ClosureModal UI and the
 * post-close narrative template read from it.
 *
 * Philosophy per the addendum and design principles doc:
 *   A failed-close surfaces missing elements as a CHECKLIST, not a
 *   wall of red. Principle P1 (Zero Validation Rage) applies — the
 *   crew is stressed, not dumb.
 *
 * Each rule produces:
 *   ok:      boolean   did the record satisfy the rule
 *   label:   string    what the rule checks (user-facing)
 *   detail:  string?   hint if failed
 *   blocker: boolean   if true, prevents session close until satisfied
 *                      (non-blockers are best-practice reminders)
 */

export interface ComplianceResult {
  ok: boolean;
  label: string;
  detail?: string;
  blocker: boolean;
}

export interface ComplianceReport {
  results: ComplianceResult[];
  canClose: boolean;
  blockersFailing: number;
}

// ------------------------------------------------------------------
// Helpers on the event log
// ------------------------------------------------------------------
function firstOf(events: Code99Event[], type: Code99Event['event_type']) {
  return events.slice().reverse().find((e) => e.event_type === type);
}
function anyOf(events: Code99Event[], type: Code99Event['event_type']) {
  return events.some((e) => e.event_type === type);
}
function anyEpi(events: Code99Event[]) {
  return events.some((e) => e.event_type === 'medication'
    && typeof e.details?.name === 'string'
    && (e.details.name as string).toLowerCase().startsWith('epi'));
}
function withholdingEpiNoteLogged(events: Code99Event[]) {
  return events.some((e) => e.event_type === 'note'
    && typeof e.details?.text === 'string'
    && /withhold|not given|held|contraindic/i.test(e.details.text as string));
}

// ------------------------------------------------------------------
// The check
// ------------------------------------------------------------------
export function computeCompliance(
  session: Code99Session | null,
  events: Code99Event[],
): ComplianceReport {
  if (!session) {
    return { results: [], canClose: false, blockersFailing: 0 };
  }

  const results: ComplianceResult[] = [];

  // R1. CPR initiation timestamp
  results.push({
    label: 'CPR initiation timestamped',
    blocker: true,
    ok: !!session.cpr_started_at || anyOf(events, 'cpr_start'),
    detail: 'Tap "CPR ▶" on the action bar the moment compressions start — this anchors every downstream clock.',
  });

  // R2. At least one documented rhythm
  results.push({
    label: 'At least one rhythm logged',
    blocker: true,
    ok: anyOf(events, 'rhythm'),
    detail: 'Tap "Rhythm" and pick VF / PVT / PEA / Asystole / Organized / ROSC. Every intervention is tied to a rhythm — rhythmless record is incomplete.',
  });

  // R3. At least one epi dose OR a documented note justifying withholding
  results.push({
    label: 'Epinephrine given or withholding justified',
    blocker: true,
    ok: anyEpi(events) || withholdingEpiNoteLogged(events),
    detail: 'Either log at least one epinephrine dose from the Med button, or add a Note explaining why epi was withheld (contraindication, protocol deviation, etc.).',
  });

  // R4. Airway management documented
  results.push({
    label: 'Airway management documented',
    blocker: true,
    ok: anyOf(events, 'airway'),
    detail: 'Tap "Airway" — BVM is a valid answer. Every resuscitation needs an airway entry (device type + confirmation + EtCO2 if advanced).',
  });

  // R5. Final disposition set
  results.push({
    label: 'Final disposition set',
    blocker: true,
    ok: !!session.disposition,
    detail: 'Tap "ROSC", "Terminate", or mark the session as transferred care before closing. No undetermined end states.',
  });

  // R6. Termination-specific elements (only blocking if disposition = termination)
  if (session.disposition === 'termination') {
    results.push({
      label: 'Medical command physician name + badge',
      blocker: true,
      ok: !!session.med_cmd_physician_name && !!session.med_cmd_physician_badge,
      detail: 'Termination of resuscitation requires med-command attestation — enter physician name and badge via the Terminate workflow.',
    });
    results.push({
      label: 'Time of death recorded',
      blocker: true,
      ok: !!session.time_of_death,
      detail: 'Required medico-legally on any field termination.',
    });
    results.push({
      label: 'Family notification status recorded',
      blocker: true,
      ok: !!session.family_notified,
      detail: 'yes / no / unknown / deferred — all acceptable answers, just needs to be documented.',
    });
  }

  // R7. Post-ROSC specific elements (only blocking if disposition = rosc)
  if (session.disposition === 'rosc') {
    results.push({
      label: 'Post-ROSC 12-lead acquired',
      blocker: true,
      ok: !!session.post_rosc_12_lead_at ||
          events.some((e) =>
            e.event_type === 'twelve_lead' &&
            (session.activated_at ? new Date(e.event_time) >= new Date(session.activated_at) : true)),
      detail: 'Within 10 minutes of ROSC. STEMI detection routes transport to PCI-capable.',
    });
    results.push({
      label: 'Post-ROSC vital set captured',
      blocker: true,
      ok: !!session.post_rosc_vitals_captured_at,
      detail: 'First post-ROSC vitals confirm conversion was sustained. Mark via Session → mark post-ROSC vitals captured, or log on the main vitals tab after exiting Code 99.',
    });
  }

  // R8. Reversible causes considered (informational, not blocker)
  results.push({
    label: 'H\'s & T\'s addressed',
    blocker: false,
    ok: events.some((e) =>
      e.event_type === 'reversible_cause' && e.details?.status === 'addressed',
    ),
    detail: 'Best practice — tap through the H\'s & T\'s panel to mark each considered / addressed. Non-blocking but strengthens the record.',
  });

  const blockersFailing = results.filter((r) => r.blocker && !r.ok).length;
  return {
    results,
    canClose: blockersFailing === 0,
    blockersFailing,
  };
}
